import React, { useState } from 'react';
import { Calendar, MessageSquare, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { estudios, sucursales } from '../utils/mockData';

const Agendar = () => {
  const [formData, setFormData] = useState({
    nombreMascota: '',
    especie: '',
    nombreDueno: '',
    telefono: '',
    email: '',
    estudio: '',
    sucursal: '',
    fecha: '',
    hora: '',
    notas: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validar que la fecha sea al menos 1 día después
    const selectedDate = new Date(formData.fecha);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    if (selectedDate < tomorrow) {
      toast({
        title: 'Fecha inválida',
        description: 'Debes agendar con al menos 1 día de anticipación.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Enviar datos al backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacto/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nombre: formData.nombreDueno,
          email: formData.email,
          telefono: formData.telefono,
          mensaje: formData.notas || 'Solicitud de cita para estudios veterinarios',
          tipo_formulario: 'agendar',
          nombre_mascota: formData.nombreMascota,
          especie: formData.especie,
          estudio: formData.estudio,
          sucursal: formData.sucursal,
          fecha: formData.fecha,
          hora: formData.hora
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: '¡Cita agendada!',
          description: 'Te enviaremos una confirmación por correo electrónico pronto.',
        });
        
        // Reset form
        setFormData({
          nombreMascota: '',
          especie: '',
          nombreDueno: '',
          telefono: '',
          email: '',
          estudio: '',
          sucursal: '',
          fecha: '',
          hora: '',
          notas: ''
        });
      } else {
        toast({
          title: 'Error al enviar',
          description: data.detail || 'Hubo un problema al enviar tu solicitud. Por favor intenta nuevamente.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      toast({
        title: 'Error de conexión',
        description: 'No se pudo conectar con el servidor. Por favor intenta nuevamente.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppContact = () => {
    const message = 'Hola, me gustaría agendar una cita para toma de muestras.';
    window.open(`https://wa.me/5215545547772`, '_blank');
  };

  // Calcular fecha mínima (mañana)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Agendar Cita</h1>
          <p className="text-xl opacity-90">Programa tu cita para recolección de muestras</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Opciones de contacto */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#84cc16] rounded-full mb-4">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">Agendar en línea</h3>
              <p className="text-gray-600 mb-4">Completa el formulario y recibe confirmación instantánea</p>
              <span className="text-sm text-[#84cc16] font-semibold">Opción recomendada</span>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-shadow cursor-pointer" onClick={handleWhatsAppContact}>
            <CardContent className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-4">
                <MessageSquare className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">Agendar por WhatsApp</h3>
              <p className="text-gray-600 mb-4">Contáctanos directamente y te ayudaremos</p>
              <Button className="bg-green-500 hover:bg-green-600 text-white">
                Abrir WhatsApp
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Formulario */}
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-[#1e3a8a]">Datos para tu cita</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Datos de la mascota */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Información del paciente</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de la mascota *
                    </label>
                    <Input
                      type="text"
                      name="nombreMascota"
                      placeholder="Max, Luna, etc."
                      value={formData.nombreMascota}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Especie *
                    </label>
                    <select
                      name="especie"
                      value={formData.especie}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                      required
                    >
                      <option value="">Selecciona una opción</option>
                      <option value="Canino">Canino</option>
                      <option value="Felino">Felino</option>
                      <option value="Aves">Aves</option>
                      <option value="Reptiles">Reptiles</option>
                      <option value="Otros">Otros</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Datos del dueño */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Datos de contacto</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre completo *
                    </label>
                    <Input
                      type="text"
                      name="nombreDueno"
                      placeholder="Juan Pérez"
                      value={formData.nombreDueno}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono *
                      </label>
                      <Input
                        type="tel"
                        name="telefono"
                        placeholder="554-554-7772"
                        value={formData.telefono}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correo electrónico *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        placeholder="tu@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalles de la cita */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Detalles de la cita</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estudio a realizar *
                    </label>
                    <select
                      name="estudio"
                      value={formData.estudio}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                      required
                    >
                      <option value="">Selecciona un estudio</option>
                      {estudios.map(est => (
                        <option key={est.id} value={est.id}>{est.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sucursal *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <select
                        name="sucursal"
                        value={formData.sucursal}
                        onChange={handleChange}
                        className="w-full pl-10 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                        required
                      >
                        <option value="">Selecciona una sucursal</option>
                        {sucursales.map(suc => (
                          <option key={suc.id} value={suc.id}>{suc.nombre}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha *
                      </label>
                      <Input
                        type="date"
                        name="fecha"
                        min={minDate}
                        value={formData.fecha}
                        onChange={handleChange}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Agendar con mínimo 1 día de anticipación
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hora preferida *
                      </label>
                      <Input
                        type="time"
                        name="hora"
                        min="08:00"
                        max="19:00"
                        value={formData.hora}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notas adicionales
                    </label>
                    <textarea
                      name="notas"
                      rows="3"
                      placeholder="Información adicional que consideres importante..."
                      value={formData.notas}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#84cc16] hover:bg-[#65a30d] text-white py-6 text-lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Agendando...' : 'Confirmar cita'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Información adicional */}
        <div className="mt-8 bg-blue-50 border-l-4 border-[#1e40af] rounded-lg p-6">
          <h4 className="font-semibold text-[#1e3a8a] mb-2">Importante</h4>
          <ul className="space-y-2 text-gray-600 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-[#84cc16] font-bold mt-1">•</span>
              <span>Las citas deben agendarse con al menos 1 día de anticipación.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#84cc16] font-bold mt-1">•</span>
              <span>Recibirás una confirmación por correo y WhatsApp.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#84cc16] font-bold mt-1">•</span>
              <span>Si necesitas cancelar o reagendar, contáctanos con anticipación.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#84cc16] font-bold mt-1">•</span>
              <span>Algunos estudios requieren ayuno, verifica las indicaciones.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Agendar;